import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { formatAmount } from "../../../../../../utils/formatAmount";

const PAGE_SIZE_OPTIONS = [6, 12];

const getYears = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => currentYear - i);
};

const HistoryCard = ({
  title,
  columns,
  fetchThunk,
  selector,
  canvasId,
}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [pageSize, setPageSize] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloading, setDownloading] = useState(false);

  const dispatch = useDispatch();
  const { list = [], total = 0, loading } = useSelector(selector);

  useEffect(() => {
    if (canvasId && fetchThunk) {
      dispatch(fetchThunk({ canvasId, year, page: currentPage, limit: pageSize }));
    }
  }, [canvasId, year, pageSize, currentPage, fetchThunk, dispatch]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handlePrev = () => setCurrentPage((p) => (p > 1 ? p - 1 : p));
  const handleNext = () => setCurrentPage((p) => (p < totalPages ? p + 1 : p));

  const formatAmountValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numericValue = typeof value === "number"
      ? value
      : Number(String(value).replace(/,/g, ""));
    if (!Number.isFinite(numericValue)) return value;
    return formatAmount(numericValue);
  };

  // Download & Print
  const handleDownload = async () => {
    if (!canvasId || !fetchThunk) return;
    setDownloading(true);
    try {
      const action = await dispatch(fetchThunk({ canvasId, year, page: 1, limit: 1000 }));
      const allData = action.payload?.list || [];
      const printable = `
        <html>
          <head>
            <title>${title} - ${year}</title>
            <style>
              body { font-family: Arial, sans-serif; color: #0a363c; background: white; }
              table { border-collapse: collapse; width: 100%; }
              th, td { border: 1px solid #e2bf9c; padding: 8px; text-align: center; }
              th { background: #e2bf9c; color: #0a363c; }
              h2 { margin-bottom: 1rem; }
            </style>
          </head>
          <body>
            <h2>${title} - ${year}</h2>
            <table>
              <thead>
                <tr>
                  ${columns.map(col => `<th>${col.label}</th>`).join("")}
                </tr>
              </thead>
              <tbody>
                ${allData.length === 0
                  ? `<tr><td colspan="${columns.length}">No data</td></tr>`
                  : allData.map(row => `
                    <tr>
                      ${columns.map(col => {
                        if (col.isVariance && row.varianceInfo) {
                          return `<td style="color: ${row.varianceInfo.color}">
                            ${row.varianceInfo.arrow} ${formatAmountValue(row.variance)} (${row.varianceInfo.percentage})
                          </td>`;
                        } else if (col.key === "gainOrReduce" && row.gainOrReduce) {
                          return `<td style="color: ${row.gainOrReduce.color}">
                            ${row.gainOrReduce.arrow} ${formatAmountValue(row.gainOrReduce.amount)} (${row.gainOrReduce.percentage})
                          </td>`;
                        } else if (col.isAmount) {
                          return `<td>${formatAmountValue(row[col.key])}</td>`;
                        } else {
                          return `<td>${row[col.key] ?? "-"}</td>`;
                        }
                      }).join("")}
                    </tr>
                  `).join("")}
              </tbody>
            </table>
          </body>
        </html>
      `;
      // Open print window
      const printWindow = window.open("", "_blank");
      printWindow.document.write(printable);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (e) {
      alert("Failed to fetch data for printing.");
    }
    setDownloading(false);
  };

  return (
    <div className="bg-white rounded-xl shadow p-4 mb-6 w-full max-w-md flex flex-col justify-between">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-bold text-btn-primary text-center">{title}</h3>
        <button
          onClick={handleDownload}
          title="Download & Print All"
          className="p-2 rounded-full hover:bg-gray-100 transition"
          disabled={downloading}
        >
          <ArrowDownTrayIcon className={`w-5 h-5 ${downloading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div
        className="overflow-x-auto"
        style={{ maxHeight: 360, minHeight: 100 }}
      >
        <table className="min-w-full text-sm" style={{ tableLayout: "fixed" }}>
          <thead className="sticky top-0 z-20 bg-white" style={{ height: 48 }}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-2 py-3 bg-btn-secondary text-btn-primary font-semibold whitespace-nowrap"
                  style={{ minWidth: 120, height: 48, maxWidth: 200 }}
                  title={col.label}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            style={{
              minHeight: "50px",
              height: "auto",
            }}
          >
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No data
                </td>
              </tr>
            ) : (
              list.map((row, idx) => (
                <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                  {columns.map((col) => (
                    <td key={col.key} className="px-2 py-2 text-center">
                      {col.isVariance && row.varianceInfo ? (
                        <div className="flex items-center justify-center gap-1">
                          <span
                            className="font-semibold text-base"
                            style={{ color: row.varianceInfo.color }}
                          >
                            {row.varianceInfo.arrow}
                          </span>
                          <span
                            className="font-medium"
                            style={{ color: row.varianceInfo.color }}
                          >
                            {formatAmountValue(row.variance)}
                          </span>
                          <span
                            className="text-xs font-medium"
                            style={{ color: row.varianceInfo.color }}
                          >
                            ({row.varianceInfo.percentage})
                          </span>
                        </div>
                      ) : col.key === "gainOrReduce" && row.gainOrReduce ? (
                        <div className="flex items-center justify-center gap-1">
                          <span
                            className="font-semibold text-base"
                            style={{ color: row.gainOrReduce.color }}
                          >
                            {row.gainOrReduce.arrow}
                          </span>
                          <span
                            className="font-medium"
                            style={{ color: row.gainOrReduce.color }}
                          >
                            {formatAmountValue(row.gainOrReduce.amount)}
                          </span>
                          <span
                            className="text-xs font-medium"
                            style={{ color: row.gainOrReduce.color }}
                          >
                            ({row.gainOrReduce.percentage})
                          </span>
                        </div>
                      ) : col.isAmount ? (
                        formatAmountValue(row[col.key])
                      ) : (
                        row[col.key] ?? "-"
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination*/}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-4 w-full">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            aria-label="Previous"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="text-s text-gray-700 min-w-[60px] text-center">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            aria-label="Next"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={e => { setYear(Number(e.target.value)); setCurrentPage(1); }}
            className="border rounded px-2 pr-6 py-1 text-sm"
          >
            {getYears().map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            className="border rounded px-2 pr-6 py-1 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size} Months</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default HistoryCard;